export interface Coordinates {
  coordinates: Array<{ x: number; y: number }>;
}
export class Funnel<Datum> {
  public _size = [1, 1];
  public _mouth = [1, 1];
  public _value!: (val: Datum) => number;
  public percentageValues = (data: Array<Datum>) => {
    const values = data.map(this._value);
    const weightMax = Math.max(...values);
    const percentValues = values.map(d => (d / weightMax) * 100);
    return percentValues;
  };
  public funnel = (data: Array<Datum>): Array<Coordinates & Datum> => {
    const coordinates = this.coordinatesCalculation(data);
    const result: Array<Coordinates & Datum> = [];
    for (let i = 0; i < data.length; ++i) {
      result.push({ ...data[i], coordinates: coordinates[i].coordinates });
    }
    return result;
  };

  public coordinatesCalculation(data: Array<Datum>): Array<Coordinates> {
    const w = this._size[0];
    const h = this._size[1];
    const rw = this._mouth[0]; // rect width
    const rh = this._mouth[1]; // rect height
    const tw = (w - rw) / 2; // triangle width
    const th = h - rh; // triangle height
    let height1 = 0;
    let height2 = 0;
    let height3 = 0;
    let merge = 0;
    const coordinates: Array<Coordinates> = [];
    const ratio = tw / th;
    const areaOfTrapezium = ((w + rw) / 2) * th;
    const areaOfRectangle = rw * rh;
    const totalArea = areaOfTrapezium + areaOfRectangle;
    const percentOfRectangle = (areaOfRectangle / totalArea) * 100;
    const percentages = this.percentageValues(data);
    for (let i = data.length - 1; i >= 0; i--) {
      const selectedPercentValue = percentages[i];
      if (percentOfRectangle >= selectedPercentValue) {
        height3 = (selectedPercentValue / percentOfRectangle) * rh;
        height1 = h - height3;
        if (i === data.length - 1) {
          coordinates[i] = {
            coordinates: [
              { x: (w - rw) / 2, y: height1 },
              { x: (w - rw) / 2, y: h },
              { x: (w - rw) / 2 + rw, y: h },
              { x: (w - rw) / 2 + rw, y: height1 }
            ]
          };
        } else {
          coordinates[i] = {
            coordinates: [
              { x: (w - rw) / 2, y: height1 },
              coordinates[i + 1].coordinates[0],
              coordinates[i + 1].coordinates[3],
              { x: (w - rw) / 2 + rw, y: height1 }
            ]
          };
        }
      } else {
        const areaOfElement =
          (selectedPercentValue / 100) * totalArea - areaOfRectangle;
        const a = ratio * 2;
        const b = rw * 2;
        const c = areaOfElement * 2;
        height2 = (-b + Math.sqrt(Math.pow(b, 2) - a * -c * 4)) / (a * 2);
        height1 = h - height2 - rh;
        const base = ratio * height2 * 2 + rw;
        const width = (w - base) / 2;

        if (merge === 0) {
          if (i === data.length - 1) {
            coordinates[i] = {
              coordinates: [
                { x: width, y: height1 },
                { x: (w - rw) / 2, y: th },
                { x: (w - rw) / 2, y: h },
                { x: (w - rw) / 2 + rw, y: h },
                { x: (w - rw) / 2 + rw, y: th },
                { x: base + width, y: height1 }
              ]
            };
          } else {
            coordinates[i] = {
              coordinates: [
                { x: width, y: height1 },
                { x: (w - rw) / 2, y: th },
                coordinates[i + 1].coordinates[0],
                coordinates[i + 1].coordinates[3],
                { x: (w - rw) / 2 + rw, y: th },
                { x: base + width, y: height1 }
              ]
            };
          }
        } else {
          const coIndex = coordinates[i + 1].coordinates.length === 6 ? 5 : 3;
          coordinates[i] = {
            coordinates: [
              { x: width, y: height1 },
              coordinates[i + 1].coordinates[0],
              coordinates[i + 1].coordinates[coIndex],
              { x: base + width, y: height1 }
            ]
          };
        }
        merge = 1;
      }
    }
    return coordinates;
  }

  public size(s: Array<number>): this {
    if (s.length === 2) {
      this._size = s;
    }
    return this;
  }

  public mouth(m: [number, number]): this {
    if (m.length === 2) {
      this._mouth = m;
    }
    return this;
  }

  public value(
    v: (val: Datum) => number
  ): (data: Array<Datum>) => Array<Coordinates & Datum> {
    this._value = v;
    return this.funnel;
  }
}
